import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import './TransactionHistory.css';

const TransactionHistory = () => {
  const { authToken } = useAuth();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState(null);
  const [summary, setSummary] = useState({});

  // Filters
  const [typeFilter, setTypeFilter] = useState('all');
  const [currencyFilter, setCurrencyFilter] = useState('all');
  const [periodFilter, setPeriodFilter] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const API_BASE = process.env.REACT_APP_API_URL || `http://${window.location.hostname}:5001`;
  const headers = { Authorization: `Bearer ${authToken}` };

  const fetchTransactions = useCallback(async (p) => {
    try {
      setLoading(true);
      setError('');
      const params = {
        page: p || page,
        limit: 20
      };
      if (typeFilter !== 'all') params.type = typeFilter;
      if (currencyFilter !== 'all') params.currency = currencyFilter;
      if (periodFilter !== 'all') params.period = periodFilter;
      if (periodFilter === 'custom' && startDate) params.startDate = startDate;
      if (periodFilter === 'custom' && endDate) params.endDate = endDate;

      const res = await axios.get(`${API_BASE}/api/users/transactions`, { headers, params });
      const data = res.data;

      const txns = data.transactions || [];
      setTransactions(txns);
      setPagination(data.pagination);
      setSummary(data.summary || {});
    } catch (err) {
      console.error('Error fetching transactions:', err);
      setError('Failed to load transactions. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [page, typeFilter, currencyFilter, periodFilter, startDate, endDate]);

  useEffect(() => {
    setPage(1);
    fetchTransactions(1);
  }, [typeFilter, currencyFilter, periodFilter, startDate, endDate]);

  useEffect(() => {
    fetchTransactions(page);
  }, [page]);

  const handleFilterChange = (setter) => (e) => {
    setter(e.target.value);
    setPage(1);
  };

    const isCredit = (type) => {
    const creditTypes = ['deposit', 'game_winnings', 'game_win', 'bonus', 'game_refund', 'admin_deposit', 'win', 'credit', 'refund'];
    return creditTypes.includes((type || '').toLowerCase());
  };

  const isDebit = (type) => {
    const debitTypes = ['withdrawal', 'game_entry', 'admin_withdrawal', 'fee', 'debit', 'entry', 'game_fee'];
    return debitTypes.includes((type || '').toLowerCase());
  };

  const getDirection = (type) => {
    if (isCredit(type)) return 'credit';
    if (isDebit(type)) return 'debit';
    const amt = parseFloat(type?.amount) || 0;
    return amt >= 0 ? 'credit' : 'debit';
  };

  const formatAmount = (amount, currency) => {
    const num = parseFloat(amount) || 0;
    const symbols = { USD: '$', INR: '\u20b9', LRD: 'L$', CFA: 'CFA' };
    const sym = symbols[currency?.toUpperCase()] || currency || '';
    if (currency?.toUpperCase() === 'CFA') return `${num.toFixed(2)} ${sym}`;
    return `${sym}${num.toFixed(2)}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const classes = {
      'completed': 'status-completed',
      'pending': 'status-pending',
      'failed': 'status-failed',
      'cancelled': 'status-cancelled'
    };
    return <span className={`tx-status ${classes[status] || ''}`}>{status || 'completed'}</span>;
  };

  // Summary by currency from transactions
  const getSummaryByCurrency = () => {
    const byCurrency = {};
    (transactions || []).forEach(tx => {
      const cur = tx.currency || 'USD';
      if (!byCurrency[cur]) {
        byCurrency[cur] = { credit: 0, debit: 0 };
      }
      const type = (tx.type || '').toLowerCase();
      const amt = parseFloat(tx.amount) || 0;
      if (isCredit(type)) byCurrency[cur].credit += Math.abs(amt);
      else if (isDebit(type)) byCurrency[cur].debit += Math.abs(amt);
      else if (amt >= 0) byCurrency[cur].credit += Math.abs(amt);
      else byCurrency[cur].debit += Math.abs(amt);
    });
    return byCurrency;
  };

  const summaryByCurrency = summary.byCurrency || getSummaryByCurrency();

  return (
    <div className="tx-container">
      <div className="tx-header">
        <h1>Transaction History</h1>
        <p>View all your deposits, withdrawals, winnings and gaming fees</p>
      </div>

      {error && <div className="tx-error">{error}</div>}

            {/* Summary Cards */}
      {Object.keys(summaryByCurrency).length > 0 && (
        <div className="tx-summary">
          {Object.entries(summaryByCurrency).map(([currency, data]) => (
            <div key={currency} className={`summary-card summary-${currency.toLowerCase()}`}>
              <h4>{currency}</h4>
              <div className="summary-stats">
                {data.credit > 0 && <div className="summary-row"><span>Total Credit</span><span className="positive">{formatAmount(data.credit, currency)}</span></div>}
                {data.debit > 0 && <div className="summary-row"><span>Total Debit</span><span>{formatAmount(data.debit, currency)}</span></div>}
                <div className="summary-row total">
                  <span>Net</span>
                  <span className={data.credit - data.debit >= 0 ? 'positive' : ''}>
                    {formatAmount(data.credit - data.debit, currency)}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="tx-filters">
        <div className="filter-group">
          <label>Type</label>
          <select value={typeFilter} onChange={handleFilterChange(setTypeFilter)}>
            <option value="all">All Types</option>
            <option value="deposit">Deposits</option>
            <option value="withdrawal">Withdrawals</option>
            <option value="game_win">Winnings</option>
            <option value="game_entry">Game Fees</option>
            <option value="bonus">Bonuses</option>
            <option value="game_refund">Refunds</option>
            <option value="admin_deposit">Admin Deposits</option>
            <option value="admin_withdrawal">Admin Withdrawals</option>
          </select>
        </div>
        <div className="filter-group">
          <label>Currency</label>
          <select value={currencyFilter} onChange={handleFilterChange(setCurrencyFilter)}>
            <option value="all">All Currencies</option>
            <option value="USD">USD</option>
            <option value="INR">INR</option>
            <option value="LRD">LRD</option>
            <option value="CFA">CFA</option>
          </select>
        </div>
        <div className="filter-group">
          <label>Period</label>
          <select value={periodFilter} onChange={handleFilterChange(setPeriodFilter)}>
            <option value="all">All Time</option>
            <option value="daily">Today</option>
            <option value="weekly">This Week</option>
            <option value="monthly">This Month</option>
            <option value="custom">Custom Range</option>
          </select>
        </div>
        {periodFilter === 'custom' && (
          <>
            <div className="filter-group">
              <label>From</label>
              <input type="date" value={startDate} onChange={(e) => { setStartDate(e.target.value); setPage(1); }} />
            </div>
            <div className="filter-group">
              <label>To</label>
              <input type="date" value={endDate} onChange={(e) => { setEndDate(e.target.value); setPage(1); }} />
            </div>
          </>
        )}
      </div>

      {/* Transaction List */}
      <div className="tx-list-wrapper">
        {loading && transactions.length === 0 && (
          <div className="tx-loading">Loading transactions...</div>
        )}
                {!loading && transactions.length === 0 && (
          <div className="tx-empty">
            <p>No transactions found.</p>
          </div>
        )}
        {transactions.length > 0 && (
          <>
            <div className="tx-list-header">
              <span className="tx-col-date">Date</span>
              <span className="tx-col-type">Debit/Credit</span>
              <span className="tx-col-desc">Description</span>
              <span className="tx-col-amount">Amount</span>
              <span className="tx-col-status">Status</span>
            </div>
            <div className="tx-list">
              {transactions.map((tx, i) => {
                const credit = isCredit(tx.type);
                return (
                  <div key={tx.id || i} className="tx-item">
                    <div className="tx-date">{formatDate(tx.created_at)}</div>
                    <div className="tx-type">
                      {credit ? (
                        <span className="tx-badge-credit">CREDIT</span>
                      ) : (
                        <span className="tx-badge-debit">DEBIT</span>
                      )}
                    </div>
                    <div className="tx-desc">{tx.description || tx.notes || '-'}</div>
                    <div className={`tx-amount ${credit ? 'amount-credit' : 'amount-debit'}`}>
                      {credit ? '+' : '-'}{formatAmount(tx.amount, tx.currency)}
                    </div>
                    <div className="tx-status-cell">{getStatusBadge(tx.status)}</div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <div className="tx-pagination">
          <button
            className="page-btn"
            disabled={!pagination.hasPrev}
            onClick={() => setPage(p => Math.max(1, p - 1))}
          >
            \u2190 Previous
          </button>
          <div className="page-info">
            {[...Array(Math.min(pagination.totalPages, 5))].map((_, i) => {
              const startPage = Math.max(1, page - 2);
              const pNum = startPage + i;
              if (pNum > pagination.totalPages) return null;
              return (
                <button
                  key={pNum}
                  className={`page-num ${pNum === page ? 'active' : ''}`}
                  onClick={() => setPage(pNum)}
                >
                  {pNum}
                </button>
              );
            })}
          </div>
          <button
            className="page-btn"
            disabled={!pagination.hasNext}
            onClick={() => setPage(p => p + 1)}
          >
            Next \u2192
          </button>
        </div>
      )}
    </div>
  );
};

export default TransactionHistory;